import { useModal, type ModalConfig } from '../../contexts/ModalContext'
import { cloneElement, isValidElement, type ReactElement, useEffect, useRef } from 'react'
import './ModalContainer.css'

/**
 * Modal Wrapper Component
 * Wraps each modal to handle auto-focus
 */
function ModalWrapper({ children, modalId }: { readonly children: ReactElement; readonly modalId: string }) {
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Auto-focus the modal when it mounts
    if (wrapperRef.current) {
      // Find the first focusable element in the modal or focus the wrapper
      const focusableElement = wrapperRef.current.querySelector<HTMLElement>(
        '[tabindex]:not([tabindex="-1"]), button, [href], input, select, textarea'
      )
      if (focusableElement) {
        focusableElement.focus()
      } else {
        // If no focusable element found, focus the wrapper itself
        wrapperRef.current.focus()
      }
    }
  }, [])

  return (
    <div ref={wrapperRef} data-modal-id={modalId} style={{ outline: 'none' }}>
      {children}
    </div>
  )
}

/**
 * Modal Container Component
 * Renders all open modals - react-bootstrap Modal handles its own portaling
 */
export function ModalContainer() {
  const { openedModals, hideModal } = useModal()
  const modalEntries = Object.entries(openedModals)

  if (modalEntries.length === 0) {
    return null
  }

  // Add class for modal count (used by CSS to handle backdrop stacking)
  const containerClass = `modals-container contains-modals-${modalEntries.length}`

  // Don't use createPortal - let react-bootstrap Modal handle its own portaling
  return (
    <div className={containerClass} data-modal-count={modalEntries.length}>
      {modalEntries.map(([id, config]: [string, ModalConfig], index) => {
        // Clone the modal component and inject onClose handler
        const modalComponent = isValidElement(config.component)
          ? cloneElement(config.component as ReactElement, {
              key: id,
              // @ts-expect-error - Dynamic prop injection for onClose handler
              onClose: (data) => {
                // Call the original onClose if it exists
                // @ts-expect-error - Dynamic props access
                const originalOnClose = (config.component as ReactElement).props?.onClose
                if (originalOnClose) {
                  originalOnClose(data)
                }
                // Then hide the modal
                hideModal(id)
              },
              modalStackIndex: index
            })
          : config.component

        return (
          <ModalWrapper key={id} modalId={id}>
            {modalComponent as ReactElement}
          </ModalWrapper>
        )
      })}
    </div>
  )
}

export default ModalContainer
